import api from './api';

const accountingService = {
  // =============================================================================
  // SERVICES CLASSES COMPTABLES
  // =============================================================================
  
  // Récupérer toutes les classes
  getClasses: async () => {
    try {
      const response = await api.get('/accounting/classes');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des classes:', error);
      throw error.response?.data || error;
    }
  },

  // Créer une nouvelle classe
  createClasse: async (classeData) => {
    try {
      const response = await api.post('/accounting/classes', classeData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la classe:', error);
      throw error.response?.data || error;
    }
  },

  // Modifier une classe
  updateClasse: async (id, classeData) => {
    try {
      const response = await api.put(`/accounting/classes/${id}`, classeData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la modification de la classe:', error);
      throw error.response?.data || error;
    }
  },

  // Supprimer une classe
  deleteClasse: async (id) => {
    try {
      const response = await api.delete(`/accounting/classes/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de la classe:', error);
      throw error.response?.data || error;
    }
  },

  // =============================================================================
  // SERVICES RUBRIQUES COMPTABLES
  // =============================================================================

  // Récupérer toutes les rubriques
  getRubriques: async (classeId = null) => {
    try {
      const params = classeId ? { classe_id: classeId } : {};
      const response = await api.get('/accounting/rubriques', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des rubriques:', error);
      throw error.response?.data || error;
    }
  },

  // Créer une nouvelle rubrique
  createRubrique: async (rubriqueData) => {
    try {
      const response = await api.post('/accounting/rubriques', rubriqueData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la rubrique:', error);
      throw error.response?.data || error;
    }
  },

  // Modifier une rubrique
  updateRubrique: async (id, rubriqueData) => {
    try {
      const response = await api.put(`/accounting/rubriques/${id}`, rubriqueData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la modification de la rubrique:', error);
      throw error.response?.data || error;
    }
  },

  // Supprimer une rubrique
  deleteRubrique: async (id) => {
    try {
      const response = await api.delete(`/accounting/rubriques/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de la rubrique:', error);
      throw error.response?.data || error;
    }
  },

  // =============================================================================
  // SERVICES COMPTES COMPTABLES
  // =============================================================================

  // Récupérer tous les comptes
  getComptes: async (filters = {}) => {
    try {
      const response = await api.get('/accounting/comptes', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des comptes:', error);
      throw error.response?.data || error;
    }
  },

  // Créer un nouveau compte
  createCompte: async (compteData) => {
    try {
      const response = await api.post('/accounting/comptes', compteData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du compte:', error);
      throw error.response?.data || error;
    }
  },

  // Modifier un compte
  updateCompte: async (id, compteData) => {
    try {
      const response = await api.put(`/accounting/comptes/${id}`, compteData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la modification du compte:', error);
      throw error.response?.data || error;
    }
  },

  // Supprimer/Désactiver un compte
  deleteCompte: async (id) => {
    try {
      const response = await api.delete(`/accounting/comptes/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression du compte:', error);
      throw error.response?.data || error;
    }
  },

  // =============================================================================
  // SERVICES UTILITAIRES
  // =============================================================================

  // Récupérer le plan comptable complet
  getPlanComptable: async (exportFormat = null) => {
    try {
      const params = exportFormat ? { export: exportFormat } : {};
      const response = await api.get('/accounting/plan-comptable', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du plan comptable:', error);
      throw error.response?.data || error;
    }
  },

  // Rechercher dans le plan comptable
  search: async (query, type = null) => {
    try {
      const params = { q: query };
      if (type) params.type = type;
      
      const response = await api.get('/accounting/search', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      throw error.response?.data || error;
    }
  },

  // Importer des données depuis un fichier Excel
// Importer des données depuis un fichier Excel
importFromExcel: async (file) => {
  try {
    console.log('Préparation upload:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    const formData = new FormData();
    formData.append('file', file, file.name);

    // Configuration axios spécifique pour l'upload
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes
      maxContentLength: 10 * 1024 * 1024, // 10MB
      maxBodyLength: 10 * 1024 * 1024, // 10MB
    };

    console.log('Envoi de la requête...');
    const response = await api.post('/accounting/import', formData, config);
    console.log('Réponse reçue:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('Erreur upload:', error);
    
    if (error.code === 'ERR_NETWORK') {
      throw new Error('Problème de connexion au serveur. Vérifiez que le serveur backend fonctionne.');
    }
    
    if (error.response) {
      throw error.response.data;
    }
    
    throw new Error(error.message || 'Erreur inconnue lors de l\'upload');
  }
},

  // =============================================================================
  // HELPERS POUR L'INTERFACE
  // =============================================================================

  // Valider un code de classe (1-9)
  validateClasseCode: (code) => {
    return /^[1-9]$/.test(code);
  },

  // Valider un code de rubrique (10-99)
  validateRubriqueCode: (code, classeCode) => {
    const regex = new RegExp(`^${classeCode}\\d$`);
    return regex.test(code);
  },

  // Valider un code de compte
  validateCompteCode: (code, classeCode) => {
    return code && code.startsWith(classeCode) && code.length >= 1;
  },

  // Formatter l'affichage d'un compte avec indentation
  formatCompteDisplay: (compte) => {
    const indent = '  '.repeat(compte.niveau - 1);
    return `${indent}${compte.code} - ${compte.libelle}`;
  },

  // Obtenir la couleur selon le niveau pour l'affichage
  getNiveauColor: (niveau) => {
    const colors = {
      1: 'bg-blue-100 text-blue-800',     // Classes
      2: 'bg-green-100 text-green-800',   // Rubriques
      3: 'bg-yellow-100 text-yellow-800', // Comptes niveau 3
      4: 'bg-purple-100 text-purple-800', // Comptes niveau 4
      5: 'bg-gray-100 text-gray-800'      // Comptes niveau 5
    };
    return colors[niveau] || 'bg-gray-100 text-gray-800';
  },

  // Construire l'arborescence pour l'affichage hiérarchique
  buildTreeStructure: (items) => {
    const map = new Map();
    const roots = [];

    // Créer le map des items
    items.forEach(item => {
      map.set(item._id, { ...item, children: [] });
    });

    // Construire la hiérarchie
    items.forEach(item => {
      const node = map.get(item._id);
      if (item.parent_id) {
        const parent = map.get(item.parent_id);
        if (parent) {
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      } else {
        roots.push(node);
      }
    });

    return roots;
  }
};

export default accountingService;